import React from "react";
import { Button, Dropdown, Menu, Space } from "antd";
import Icon from "@ant-design/icons";
import { filter, isEmpty, map, size } from "lodash";
import * as Icons from "assets/icons";
import { AICC_LANGUAGE_ACTION_LABEL, AiccLanguageAction } from "constants/aiccLanguage";
import { AiccManagedLanguage } from "types/model/aiccLanguage";
import { languageMenuActions } from "../languageStatus";
import styles from "./LanguagePanel.module.scss";

interface Props {
  language: AiccManagedLanguage;
  onAction?: Partial<Record<AiccLanguageAction, (language: AiccManagedLanguage) => void>>;
  onViewVersionHistory?: (language: AiccManagedLanguage) => void;
}

/**
 * What belongs to the language rather than to one of its versions: the history of everything it
 * has published, and deleting the whole thing. Version actions live on the rows, which is the
 * only place they can name what they act on without ambiguity.
 *
 * The history is a plain link because it only reads; Delete stays behind ⋯ because it does not.
 */
function LanguagePanelActions({ language, onAction, onViewVersionHistory }: Props) {
  const actions = filter(languageMenuActions(language), (action) => !!onAction?.[action]);
  const versionCount = size(language.versions);

  const overflowMenu = (
    <Menu>
      {map(actions, (action) => (
        <Menu.Item key={action} onClick={() => onAction?.[action]?.(language)}>
          {AICC_LANGUAGE_ACTION_LABEL[action]}
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <Space size={12}>
      {/*
        Out of the menu and onto the card: reading what a language has released is the one thing
        here that changes nothing, and burying it behind ⋯ made it the hardest to find. The menu
        keeps only the destructive action.
      */}
      {!!versionCount && (
        <Button
          type="link"
          className={styles.linkButton}
          disabled={!onViewVersionHistory}
          onClick={() => onViewVersionHistory?.(language)}
        >
          {`Version History (${versionCount})`}
        </Button>
      )}

      {!isEmpty(actions) && (
        <Dropdown overlay={overflowMenu} placement="bottomRight" trigger={["click"]}>
          <Icon component={Icons.More} className={styles.overflowIcon} />
        </Dropdown>
      )}
    </Space>
  );
}

export default LanguagePanelActions;
